import {useEffect, useRef, useState} from 'react';
import ClientInfoForm from './ClientInfoForm';
import AurButton from '../../common/aur-button/AurButton';
import {Box, Grid, Typography} from '@mui/material';
import ClientProjectSelection from '../add-new-team-member/ClientProjectSelection';
import {ProjectDetails} from '../../pages/user-management/models';
import ClientAccordionViewContainer from './ClientAccordionViewContainer';
import {
  createUserRole,
  getActiveProjectList,
  getHighlyConfidentialProjectsList,
  getPermissionGroupList,
  updateUserRole,
  getDomainWhitelist,
} from '../../../api/ClientManagementService';
import {Option} from '../../common/aur-select/AurSelect';
import {useDispatch, useSelector} from 'react-redux';
import {getRoles} from '../../../redux/slices/roleListSlice';
import {useToast} from '../../../contexts/toastContext/ToastContext';
import {t} from 'i18next';
import AurDialog from '../../common/aur-dialog/AurDialog';
import {ClientProject} from '../../../types/project-type/projectDetailsType';
import {UserRoleIds} from '../../../types/user-management/userManagementTypes';
import {RootState, store} from '../../../redux/store';
import {PermissionGroup} from '../../../types/permission-group/permissionGroup';
import PermissionGroupDrawer from '../../permission-groups/PermissionGroupDrawer';
import AurAccordion, {
  AurAccordionRef,
} from '../../common/aur-accordion/AurAccordion';
import {getPermissionDetail} from '../../pages/user-management/tabs/utils';
import {
  BUDGET_ESTIMATED,
  EVIDENCE_SOURCE_MANAGEMENT,
  PERMISSION_GROUP_CATEGORY,
} from '../../../constants/constants';
import {Permission} from '../../../types/permission-group/permission';
import AurLoadingIndicator from '../../common/aur-loading-indicator/AurLoadingIndictor';
import {useModal} from '../../../contexts/modalContext/ModalContext';
import {CreateUserRoleRequest} from '../../../types/client-management/cientManagementTypes';
import {UserRolesData} from '../../../redux/slices/userRoleSlice';
import {
  clearNewlyAddedPermissionGroup,
  setRefreshPermissionGroups,
} from '../../../redux/slices/permissionGroupSlice';
import {
  isNewlyAddedPermissionGroupForClient,
  mergePermissionGroupsWithWriteModel,
  permissionGroupToItemType,
  upsertPermissionGroupItem,
} from '../../../utils/permissionGroupItemUtils';
import * as psl from 'psl';
import {ItemType} from '../../pages/user-management/tabs/AddClientInfo';

export type ClientDetailsInfo = {
  client: string;
  clientId: string;
  role: string;
  roleId: string;
  permissionGroup: string | undefined;
  permissionGroupId: string | undefined;
  projectList: ProjectDetails[] | undefined;
  id?: number;
  isExpanded?: boolean;
  isAdmin?: boolean;
  createdBy?: string;
  createdDate?: Date;
  updatedBy?: string;
  updatedDate?: Date;
  userRoleId?: string;
  budgetConsumptionEnabled?: boolean;
};

type DomainWhitelistItem = {
  clientDomainId: string;
  clientId: string;
  domainName: string;
  isDefault: boolean;
};

type Mode = 'create' | 'edit' | 'view';

type ClientAndProjectFormContainerProps = Readonly<{
  clientDetailsInfo: ClientDetailsInfo;
  savedClientList?: ClientDetailsInfo[];
  permissionFromDB?: Permission[];
  teamMemberId?: string;
  teamMemberEmail?: string;
  mode: Mode;
  isControlDisabled?: boolean;
  showHCProjects?: boolean;
  onEditButtonClicked: (id: number) => void;
  onDeleteButtonClicked: (id: number) => void;
  onSaveButtonClicked?: (savedUserRole?: UserRolesData) => void;
  onCancelButtonClicked?: () => void;
  onProjectSavedOrCanceled?: (savedUserRole?: UserRolesData) => void;
  setShowHCProjects?: (value: boolean) => void;
  defaultSelectedClientId?: string;
  onEditProject?: (id: number, isEdit: boolean) => void;
}>;

import {permissionGroupSelector} from '../../../redux/slices/permissionGroupSlice';

function ClientAndProjectFormContainer(
  props: ClientAndProjectFormContainerProps
) {
  const {
    clientDetailsInfo,
    savedClientList = [],
    teamMemberId = '',
    teamMemberEmail = '',
    mode,
    isControlDisabled = false,
    showHCProjects = false,
    permissionFromDB,
    onEditButtonClicked,
    onDeleteButtonClicked,
    onSaveButtonClicked,
    onCancelButtonClicked,
    onProjectSavedOrCanceled,
    setShowHCProjects,
    defaultSelectedClientId,
    onEditProject,
  } = props;
  const {showToast} = useToast();
  const dispatch = useDispatch();
  // ----------- Client creating mode states start -------------
  const roleList = useSelector(getRoles);
  const [clientProjects, setClientProjects] = useState<ClientProject[]>([]);
  const [clientHCProjects, setClientHCProjects] = useState<ClientProject[]>([]);
  const [unsavedProjectList, setUnsavedProjectList] = useState<
    ProjectDetails[]
  >([]);
  const [showLoader, setShowLoader] = useState(false);
  // ----------- Client creating mode states end -------------

  const [roleOptions, setRoleOptions] = useState<Option[]>([]);
  const [selectedClientId, setSelectedClientId] = useState(
    defaultSelectedClientId ?? ''
  );
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [selectedHCProjectId, setSelectedHCProjectId] = useState('');
  const [selectedProjPermissionGroupId, setSelectedProjPermissionGroupId] =
    useState('');
  const [selectedClientPermissionGroupId, setSelectedClientPermissionGroupId] =
    useState('');
  const [, setIsWorkspace] = useState<boolean>(false);
  const [isAdminRoleSelected, setIsAdminRoleSelected] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showPromoteModal, setShowPromoteModal] = useState(false);
  const [showDemoteModal, setShowDemoteModal] = useState(false);
  const [showPermissionGroupDropdown] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [selectedPermissionGroup, setSelectedPermissionGroup] = useState<
    PermissionGroup | null | undefined
  >(null);
  const [clientPermissionGroupOptionList, setClientPermissionGroupOptionList] =
    useState<ItemType[]>([]);
  const [projPermissionGroupOptionList, setProjPermissionGroupOptionList] =
    useState<ItemType[]>([]);
  const [initialHCState] = useState(showHCProjects);
  const [clientBudgetConsumptionEnabled, setClientBudgetConsumptionEnabled] =
    useState<boolean>(false);
  const [showClientAdminModal, setShowClientAdminModal] =
    useState<boolean>(false);

  const evidenceCodes = [
    EVIDENCE_SOURCE_MANAGEMENT.STAGING_AREA_ACCESS,
    EVIDENCE_SOURCE_MANAGEMENT.EVIDENCE_SOURCE_UPLOAD_ACCESS,
    EVIDENCE_SOURCE_MANAGEMENT.INITIATE_PROCESSING_ACCESS,
  ];
  const permissionGroups = useSelector(permissionGroupSelector);
  const userRoles = useSelector((rootState: RootState) => rootState.userRole);
  const [permissionGroupOpen, setPermissionGroupOpen] = useState(false);
  const [category, setCategory] = useState<PERMISSION_GROUP_CATEGORY>(
    PERMISSION_GROUP_CATEGORY.CLIENT
  );
  const [createProjectDetails, setCreateProjectDetails] =
    useState<ProjectDetails | null>(null);

  const refreshPermissionGroups = useSelector(
    (state: RootState) => state.permissionGroups.refreshPermissionGroups
  );
  const {newlyAddedPermissionGroupId, newlyAddedPermissionGroupCategory} =
    useSelector((state: RootState) => state.permissionGroups);

  const accordionRef = useRef<AurAccordionRef>(null);
  const {showModal} = useModal();

  const togglePermissionGroupsDrawer = () => {
    permissionGroupOpen
      ? setPermissionGroupOpen(false)
      : setPermissionGroupOpen(true);
  };

  // #region common methods
  const fetchRoles = () => {
    if (roleList) {
      const roleOptineList = roleList.map(
        (value: {id: string; name: string; roleCode: string}) => ({
          key: value.id,
          value: value.id,
          label: value.name,
          roleCode: value.roleCode,
        })
      );
      setRoleOptions(roleOptineList);
    }
  };

  const fetchProjects = async () => {
    if (!selectedClientId && mode === 'create') return;
    if (!clientDetailsInfo && mode === 'edit') return;

    /**
     * When system admin permission revoked for user then
     * this api get call with clientId undefine so stopping it
     */
    if (mode === 'view' && !clientDetailsInfo.clientId) return;

    try {
      const response = await getActiveProjectList(
        mode === 'create' ? selectedClientId : clientDetailsInfo.clientId
      );
      setClientProjects(response.data?.data?.data);
    } catch (e) {
      console.error(e);
    }
  };

  const fetchHCProjects = async () => {
    if (!selectedClientId && mode === 'create') return;
    if (!clientDetailsInfo && mode === 'edit') return;

    /**
     * When system admin permission revoked for user then
     * this api get call with clientId undefine so stopping it
     */
    if (mode === 'view' && !clientDetailsInfo.clientId) return;
    try {
      const response = await getHighlyConfidentialProjectsList(
        mode === 'create' ? selectedClientId : clientDetailsInfo.clientId
      );
      setClientHCProjects(response.data.data.data);
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    fetchHCProjects();
    fetchProjects();
  }, [selectedClientId]);

  // #endregion

  // #region create mode methods
  const isClientExist = () => {
    if (
      savedClientList.find(
        (item) => item.clientId.toLowerCase() == selectedClientId.toLowerCase()
      )
    ) {
      showToast(
        t('addEditTeamMember.toast.clientAlreadyExists'),
        'error',
        undefined,
        'center'
      );
      return true;
    }
    return false;
  };
  const isClientAddDisable = () => {
    if (isClientExist()) return true;
    if (isAdminRoleSelected && selectedClientId) return false;
    if (
      !selectedClientId ||
      !selectedRoleId ||
      !selectedClientPermissionGroupId
    )
      return true;
    if (
      createProjectDetails?.projectId &&
      createProjectDetails?.permissionGroupId == ''
    ) {
      return true;
    }
    if (
      createProjectDetails?.permissionGroupId &&
      createProjectDetails?.projectId == ''
    ) {
      return true;
    }
    return false;
  };

  const onClientChange = (
    clientId: string,
    clientName: string,
    budgetConsumptionEnabled: boolean
  ) => {
    const permissionGroupsState = store.getState().permissionGroups;
    if (
      permissionGroupsState.newlyAddedPermissionGroupId &&
      !isNewlyAddedPermissionGroupForClient(permissionGroupsState, clientId)
    ) {
      dispatch(clearNewlyAddedPermissionGroup());
      setSelectedProjPermissionGroupId('');
      setSelectedClientPermissionGroupId('');
      setSelectedPermissionGroup(null);
    }
    setSelectedClientId(clientId);
    setClientBudgetConsumptionEnabled(budgetConsumptionEnabled);
    clientDetailsInfo.client = clientName;
    setUnsavedProjectList([]);
    setShowHCProjects && setShowHCProjects(false);
  };
  const onRoleChange = (roleId: string) => {
    if (roleId === UserRoleIds.ClientAdmin) {
      setIsAdminRoleSelected(true);
      setUnsavedProjectList([]);
    } else {
      setIsAdminRoleSelected(false);
    }
    if (
      selectedRoleId === UserRoleIds.ClientAdmin &&
      roleId !== UserRoleIds.ClientAdmin &&
      showHCProjects &&
      createProjectDetails
    ) {
      setCreateProjectDetails(null);
      setUnsavedProjectList([]);
    }
    if (mode === 'create') {
      setShowHCProjects && setShowHCProjects(false);
    }

    setSelectedRoleId(roleId);
  };
  const onPermissionGroupChange = (
    permissionGroupId: string,
    category: PERMISSION_GROUP_CATEGORY
  ) => {
    if (category == PERMISSION_GROUP_CATEGORY.PROJECT) {
      setSelectedProjPermissionGroupId(permissionGroupId);
      const permissionGroup =
        (permissionGroups.permissionGroups.find(
          (data) => data.permissionGroupId === permissionGroupId
        ) ?? selectedPermissionGroup?.permissionGroupId === permissionGroupId)
          ? selectedPermissionGroup
          : undefined;
      if (permissionGroup) {
        setSelectedPermissionGroup(permissionGroup);
      }
    } else {
      setSelectedClientPermissionGroupId(permissionGroupId);
    }
  };

  const postNewClientData = async (data: CreateUserRoleRequest) => {
    setShowLoader(true);
    try {
      const savedUserRole = await createUserRole(teamMemberId, data);
      setShowLoader(false);
      showToast(
        t('addEditTeamMember.toast.userRoleSaved'),
        'success',
        undefined,
        'center'
      );
      onSaveButtonClicked?.(savedUserRole);
    } catch {
      setShowLoader(false);
      showToast(
        t('addEditTeamMember.toast.userRoleSaveFailed'),
        'error',
        undefined,
        'center'
      );
    }
  };

  const handleConfirmDelete = () => {
    setOpenDeleteModal(false);
    addNewClient();
  };

  const handleCancelDelete = () => {
    setOpenDeleteModal(false);
  };
  function getDomainFromEmail(email: string | null): string | null {
    if (!email) return null;

    const atIndex = email.indexOf('@');
    if (atIndex === -1) return null;

    // take everything after @
    const domain = email
      .slice(atIndex + 1)
      .trim()
      .toLowerCase();

    const parsed = psl.parse(domain);

    if (parsed && typeof parsed === 'object' && 'domain' in parsed) {
      return parsed.domain ?? null;
    }

    return null;
  }

  function getBaseDomain(hostname: string): string {
    const parsed = psl.parse(hostname);
    if ('domain' in parsed && parsed.domain) {
      return parsed.domain;
    }
    return hostname;
  }

  const saveAndContinueHandler = async () => {
    console.log({teamMemberEmail, clientDetailsInfo, teamMemberId});
    const userDomain = getDomainFromEmail(teamMemberEmail);

    const response = await getDomainWhitelist(selectedClientId);
    const whitelistItems: DomainWhitelistItem[] = response?.data?.data || [];

    const domainList: string[] = whitelistItems
      .map((item) => getBaseDomain(item.domainName))
      .filter((domain): domain is string => domain !== null);

    const clientExists = userDomain ? domainList.includes(userDomain) : false;

    if (!isAdminRoleSelected) {
      if (!clientExists) {
        console.log('Domain not whitelisted', teamMemberEmail);
        setOpenDeleteModal(true);
        return; // stop here =>   wait for user action
      }
    }
    console.log('Domain is whitelisted', teamMemberEmail);
    addNewClient();
  };

  const addNewClient = () => {
    const getProject = (p: ProjectDetails) => {
      const projectPermissions: Permission[] = [
        ...(p.projectPermissions ?? []),
      ].filter(Boolean);
      if (p.permissionCodeBudgetConsumption === true) {
        projectPermissions?.push(
          getPermissionDetail(
            BUDGET_ESTIMATED.REPORT_BUDGET_CONSUMPTION,
            permissionFromDB
          ) as never
        );
      }
      if (p.permissionCodeBudgetAlert === true) {
        projectPermissions?.push(
          getPermissionDetail(
            BUDGET_ESTIMATED.REPORT_BUDGET_ALERT,
            permissionFromDB
          ) as never
        );
      }
      evidenceCodes.forEach((code) => {
        const permission = getPermissionDetail(code, permissionFromDB);
        if (!permission) return;
        const isToggleEnabled =
          projectPermissions?.some((p) => p.permissionCode === code) ?? false;
        const exists = projectPermissions.some(
          (p) => p.permissionCode === code
        );

        if (isToggleEnabled && !exists) {
          projectPermissions.push(permission);
        }
        if (!isToggleEnabled && exists) {
          const index = projectPermissions.findIndex(
            (p) => p.permissionCode === code
          );
          projectPermissions.splice(index, 1);
        }
      });
      return [
        {
          id: p.projectId,
          name: p.projectName,
          projectPermissions: projectPermissions,
          domainCountryCode: '',
          dataCenterName: '',
          countryName: '',
          permissionGroupId: p.permissionGroupId ? p.permissionGroupId : null,
          workspaces: p.workspaces.map((w) => {
            return {
              id: w.workspaceId,
              name: w.workspaceName,
            };
          }),
        },
      ];
    };

    const requestObject = {
      roleId: selectedRoleId,
      clientId: selectedClientId,
      permissionGroups: [selectedClientPermissionGroupId].filter(Boolean),
      projects:
        (isAdminRoleSelected && !showHCProjects) || !createProjectDetails
          ? []
          : getProject(createProjectDetails),
    };
    postNewClientData(requestObject);
  };

  // #endregion

  useEffect(() => {
    if (mode === 'create') {
      dispatch(clearNewlyAddedPermissionGroup());
    }
  }, [mode, dispatch]);

  useEffect(() => {
    fetchPermissionGroups();
  }, [mode, selectedClientId, refreshPermissionGroups]);

  useEffect(() => {
    const clientID =
      mode === 'create' ? selectedClientId : clientDetailsInfo.clientId;
    const permissionGroupsState = store.getState().permissionGroups;
    if (
      !newlyAddedPermissionGroupId ||
      !newlyAddedPermissionGroupCategory ||
      !isNewlyAddedPermissionGroupForClient(permissionGroupsState, clientID)
    ) {
      return;
    }

    const newGroup = permissionGroups.permissionGroups.find(
      (group) =>
        group.permissionGroupId === newlyAddedPermissionGroupId &&
        group.category === newlyAddedPermissionGroupCategory &&
        group.clientId === clientID
    );
    if (newGroup) {
      if (
        newlyAddedPermissionGroupCategory == PERMISSION_GROUP_CATEGORY.CLIENT
      ) {
        setClientPermissionGroupOptionList((prev) =>
          upsertPermissionGroupItem(prev, permissionGroupToItemType(newGroup))
        );
      } else {
        setProjPermissionGroupOptionList((prev) =>
          upsertPermissionGroupItem(prev, permissionGroupToItemType(newGroup))
        );
        setSelectedPermissionGroup(newGroup);
      }
    }

    onPermissionGroupChange(
      newlyAddedPermissionGroupId,
      newlyAddedPermissionGroupCategory
    );
    if (mode == 'create') {
      dispatch(clearNewlyAddedPermissionGroup());
    }
  }, [
    newlyAddedPermissionGroupId,
    newlyAddedPermissionGroupCategory,
    permissionGroups.permissionGroups,
    mode,
    selectedClientId,
    clientDetailsInfo.clientId,
  ]);

  const applySavedPermissionGroupToForm = (
    savedGroup: PermissionGroup,
    groupCategory: PERMISSION_GROUP_CATEGORY
  ) => {
    const item = permissionGroupToItemType(savedGroup);
    if (groupCategory === PERMISSION_GROUP_CATEGORY.PROJECT) {
      setProjPermissionGroupOptionList((prev) =>
        upsertPermissionGroupItem(prev, item)
      );
      setSelectedPermissionGroup(savedGroup);
      setSelectedProjPermissionGroupId(savedGroup.permissionGroupId);
    } else {
      setClientPermissionGroupOptionList((prev) =>
        upsertPermissionGroupItem(prev, item)
      );
      setSelectedClientPermissionGroupId(savedGroup.permissionGroupId);
    }
    onPermissionGroupChange(savedGroup.permissionGroupId, groupCategory);
    dispatch(setRefreshPermissionGroups(false));
  };

  const fetchPermissionGroups = async () => {
    const clientID =
      mode === 'create' ? selectedClientId : clientDetailsInfo.clientId;
    if (clientID) {
      try {
        setShowLoader(true);
        const result = await getPermissionGroupList(
          clientID,
          PERMISSION_GROUP_CATEGORY.CLIENT
        );
        const apiGroups = result.data.data
          .permissionGroups as PermissionGroup[];
        const permissionGroupsState = store.getState().permissionGroups;
        const permissionGroups = mergePermissionGroupsWithWriteModel(
          apiGroups,
          permissionGroupsState,
          clientID,
          PERMISSION_GROUP_CATEGORY.CLIENT
        );

        const permissionGroupOptionList: ItemType[] = permissionGroups.map(
          permissionGroupToItemType
        );
        setClientPermissionGroupOptionList(permissionGroupOptionList);

        const newlyAddedId = permissionGroupsState.newlyAddedPermissionGroupId;
        if (
          newlyAddedId &&
          isNewlyAddedPermissionGroupForClient(
            permissionGroupsState,
            clientID
          ) &&
          permissionGroupsState.newlyAddedPermissionGroupCategory ===
            PERMISSION_GROUP_CATEGORY.CLIENT
        ) {
          const newGroup = permissionGroups.find(
            (group) =>
              group.permissionGroupId === newlyAddedId &&
              group.category === PERMISSION_GROUP_CATEGORY.CLIENT
          );
          if (newGroup) {
            setSelectedClientPermissionGroupId(newGroup.permissionGroupId);
          }
        } else if (mode === 'edit' && userRoles) {
          const userRolePermissionGroup = userRoles.userRoles.find(
            (userRole) => userRole.client?.clientId === clientID
          )?.userRolePermissionGroup?.[0];

          const filtererPermissionGroup = permissionGroups?.find(
            (permissionGroup) =>
              permissionGroup.permissionGroupId ===
              userRolePermissionGroup?.permissionGroup.permissionGroupId
          );
          if (filtererPermissionGroup) {
            setSelectedClientPermissionGroupId(
              filtererPermissionGroup.permissionGroupId
            );
          }
        }
        setShowLoader(false);
        fetchProjectPermissionGroups();
      } catch {
        setShowLoader(false);
        showToast(
          t('addEditTeamMember.toast.permissionGroupLoadFailed'),
          'error',
          undefined,
          'center'
        );
      }
    }
  };

  const fetchProjectPermissionGroups = async () => {
    const clientID =
      mode === 'create' ? selectedClientId : clientDetailsInfo.clientId;
    if (clientID) {
      try {
        setShowLoader(true);
        const result = await getPermissionGroupList(
          clientID,
          PERMISSION_GROUP_CATEGORY.PROJECT
        );
        const apiGroups = result.data.data
          .permissionGroups as PermissionGroup[];
        const permissionGroupsState = store.getState().permissionGroups;
        const permissionGroups = mergePermissionGroupsWithWriteModel(
          apiGroups,
          permissionGroupsState,
          clientID,
          PERMISSION_GROUP_CATEGORY.PROJECT
        );

        const permissionGroupOptionList: ItemType[] = permissionGroups.map(
          permissionGroupToItemType
        );
        setProjPermissionGroupOptionList(permissionGroupOptionList);

        const newlyAddedId = permissionGroupsState.newlyAddedPermissionGroupId;
        if (
          newlyAddedId &&
          isNewlyAddedPermissionGroupForClient(
            permissionGroupsState,
            clientID
          ) &&
          permissionGroupsState.newlyAddedPermissionGroupCategory ===
            PERMISSION_GROUP_CATEGORY.PROJECT
        ) {
          const newGroup = permissionGroups.find(
            (group) =>
              group.permissionGroupId === newlyAddedId &&
              group.category === PERMISSION_GROUP_CATEGORY.PROJECT
          );
          if (newGroup) {
            setSelectedPermissionGroup(newGroup);
            setSelectedProjPermissionGroupId(newGroup.permissionGroupId);
          }
        } else if (mode === 'edit' && userRoles) {
          const userRolePermissionGroup = userRoles.userRoles.find(
            (userRole) => userRole.client?.clientId === clientID
          )?.userRolePermissionGroup?.[0];

          const filtererPermissionGroup = permissionGroups?.find(
            (permissionGroup) =>
              permissionGroup.permissionGroupId ===
              userRolePermissionGroup?.permissionGroup.permissionGroupId
          );
          if (filtererPermissionGroup) {
            setSelectedPermissionGroup(filtererPermissionGroup);
            setSelectedProjPermissionGroupId(
              filtererPermissionGroup.permissionGroupId
            );
          }
        }
        setShowLoader(false);
      } catch {
        setShowLoader(false);
        showToast(
          t('addEditTeamMember.toast.permissionGroupLoadFailed'),
          'error',
          undefined,
          'center'
        );
      }
    }
  };

  useEffect(() => {
    if (mode == 'create') {
      fetchRoles();
    }
  }, []);

  useEffect(() => {
    if (mode === 'edit') {
      fetchRoles();
      setSelectedRoleId(clientDetailsInfo.roleId);
      if (clientDetailsInfo.permissionGroupId)
        setSelectedClientPermissionGroupId(clientDetailsInfo.permissionGroupId);
    }
  }, [mode]);

  useEffect(() => {
    if (selectedRoleId === UserRoleIds.ClientAdmin)
      setIsAdminRoleSelected(true);
    else {
      setIsAdminRoleSelected(false);
    }
  }, [selectedRoleId]);

  useEffect(() => {
    if (clientDetailsInfo.permissionGroupId)
      setSelectedClientPermissionGroupId(clientDetailsInfo.permissionGroupId);
  }, [onDeleteButtonClicked]);

  useEffect(() => {
    //this useeffect is added because after selecting client, there are multiple api calls happening and the focus is lost during the process, so adding the focus back here.
    if (!selectedClientId) return;
    if (showLoader) return;
    const roleElement = document.querySelector(
      '[data-testid="aur-test-client-dropdown"] [role="combobox"]'
    ) as HTMLElement | null;
    roleElement?.focus();
  }, [selectedClientId, showLoader]);

  const handleUserRoleChange = () => {
    if (clientDetailsInfo.roleId !== selectedRoleId) {
      if (clientDetailsInfo.roleId === UserRoleIds.ClientAdmin) {
        setShowDemoteModal(true);
        return;
      } else if (selectedRoleId === UserRoleIds.ClientAdmin) {
        setShowPromoteModal(true);
        return;
      }
    }
    handleSaveUserRole();
  };

  const handleSaveUserRole = async () => {
    const userRoleId = clientDetailsInfo.userRoleId as string;

    const data: {
      userRoleId: string;
      roleId: string;
      projects: string[];
      permissionGroups: string[];
      clientId: string;
    } = {
      userRoleId: userRoleId,
      roleId: selectedRoleId,
      projects: [],
      permissionGroups: [],
      clientId: clientDetailsInfo.clientId,
    };

    if (
      selectedClientPermissionGroupId &&
      selectedRoleId !== UserRoleIds.ClientAdmin
    ) {
      data.permissionGroups = [selectedClientPermissionGroupId];
    }

    if (
      selectedProjPermissionGroupId &&
      userRoles.userRoles.some((userRole) => userRole.userRoleId === userRoleId)
    ) {
      const projects = userRoles.userRoles.find(
        (userRole) => userRole.userRoleId === userRoleId
      )?.projectWorkspaceList;
      data.projects = projects as never;
    }

    if (
      selectedRoleId == UserRoleIds.ClientAdmin &&
      showHCProjects &&
      selectedHCProjectId
    ) {
      const selectedProject = clientHCProjects.find(
        (p) => p?.projectId?.toLowerCase() == selectedHCProjectId?.toLowerCase()
      );
      const projects = [
        {
          id: selectedProject?.projectId.toLowerCase(),
          name: selectedProject?.projectName,
          projectPermissions: [],
          domainCountryCode: '',
          dataCenterName: '',
          countryName: '',
          permissionGroupId: null,
          workspaces: [],
        },
      ];
      data.projects = projects as never;
    }

    try {
      const savedUserRole = await updateUserRole(userRoleId, data);
      showToast(
        t('addEditTeamMember.toast.clientDetailsUpdated'),
        'success',
        undefined,
        'center'
      );
      onSaveButtonClicked?.(savedUserRole);
    } catch (e) {
      console.error(e);
      showToast(
        t('addEditTeamMember.toast.errorSavingUser'),
        'error',
        undefined,
        'center'
      );
    }
  };

  const handleCancelUserRole = () => {
    if (isFormValid()) setShowCancelModal(true);
    else onCancelButtonClicked && onCancelButtonClicked();
  };

  const handleCancelModalCancel = () => {
    setShowCancelModal(false);
  };

  const handleCancelModalOkay = () => {
    setShowCancelModal(false);
    onCancelButtonClicked && onCancelButtonClicked();
  };

  const handleDemoteModalCancel = () => {
    setShowDemoteModal(false);
  };

  const handleDemoteModalOkay = () => {
    setShowDemoteModal(false);
    handleSaveUserRole();
  };

  const handlePromoteModalCancel = () => {
    setShowPromoteModal(false);
  };

  const handlePromoteModalOkay = () => {
    setShowPromoteModal(false);
    handleSaveUserRole();
  };

  const handleClientAdminModalCancel = () => {
    setShowClientAdminModal(false);
  };

  const handleClientAdminModalOkay = () => {
    setShowClientAdminModal(false);
    saveAndContinueHandler();
  };

  class FieldState {
    hasChanged = false;
    isValid = false;
  }

  const getRoleAndPermissionFieldState = (): FieldState => {
    const newRoleId = selectedRoleId;
    const oldRoleId = clientDetailsInfo.roleId;
    const isSameRole = newRoleId == oldRoleId;

    const isClientAdminRole = newRoleId == UserRoleIds.ClientAdmin;
    const isSystemAdminRole = newRoleId == UserRoleIds.SystemAdmin;
    const isAdminRole = isClientAdminRole || isSystemAdminRole;

    if (!newRoleId) {
      // No role selected
      return {hasChanged: false, isValid: false};
    } else if (isSameRole) {
      if (isAdminRole) {
        // The role has not changed
        // Because it is an admin we do not need to check permissions
        return {hasChanged: false, isValid: true};
      } else {
        const newClientPermId = selectedClientPermissionGroupId;
        const oldClientPermId = clientDetailsInfo.permissionGroupId;
        const isSameClientPerm = newClientPermId == oldClientPermId;
        if (!isSameClientPerm) {
          return {hasChanged: true, isValid: true};
        }
        return {hasChanged: false, isValid: false};
      }
    } else {
      return {hasChanged: true, isValid: true};
    }
  };

  const getHCFieldState = (): FieldState => {
    if (initialHCState != showHCProjects) {
      return {hasChanged: true, isValid: true};
    }
    return {hasChanged: false, isValid: true};
  };

  const isFormValid = (): boolean => {
    const rolePermissionState = getRoleAndPermissionFieldState();
    const hcState = getHCFieldState();

    if (rolePermissionState.hasChanged && rolePermissionState.isValid) {
      // Role/Permission state changed and is valid
      return true;
    }

    if (hcState.hasChanged && hcState.isValid) {
      // If the Highly Confidential state changed then the Role/Permission state must also be either:
      // 1. Changed and Valid
      // 2. Unchanged
      if (
        (rolePermissionState.hasChanged && rolePermissionState.isValid) ||
        !rolePermissionState.hasChanged
      ) {
        return true;
      }
    }

    return false;
  };

  const isClientFormModified = () => {
    return (
      selectedClientId || selectedRoleId || selectedClientPermissionGroupId
    );
  };

  const handleCancelClick = () => {
    if (isClientFormModified()) {
      showModal({
        title: t('addEditTeamMember.cancelEditPopup.title'),
        description: t('addEditTeamMember.cancelEditPopup.content'),
        labelOkButton: t('addEditTeamMember.cancelEditPopup.buttonYes'),
        labelCancelButton: t('addEditTeamMember.cancelEditPopup.buttonCancel'),
        onOk: onCancelButtonClicked,
        buttonStyle: 'sideBySide',
      });
      return;
    }
    onCancelButtonClicked && onCancelButtonClicked();
  };

  const createMode = (
    <Box className="assign-client-container">
      <Box className="assign-client-container-title typo-text headline2_size">
        {t('addEditTeamMember.assignNewClient')}
      </Box>
      <Box className="assign-client-container-body">
        <ClientInfoForm
          selectedClientId={selectedClientId}
          roleOptions={roleOptions}
          selectedRoleId={selectedRoleId}
          selectedPermissionGroupId={selectedClientPermissionGroupId}
          isEditDisabled={isControlDisabled || isClientExist()}
          isAdminRoleSelected={isAdminRoleSelected}
          hasHCProjects={clientHCProjects.length > 0}
          onClientChange={onClientChange}
          onRoleChange={onRoleChange}
          onPermissionGroupChange={(id) => {
            onPermissionGroupChange(id, PERMISSION_GROUP_CATEGORY.CLIENT);
          }}
          onCancel={() => {
            onCancelButtonClicked && onCancelButtonClicked();
          }}
          onHCProjectCheckedChange={(checked: boolean) => {
            setShowHCProjects && setShowHCProjects(checked);
          }}
          showHCProjects={showHCProjects}
          onCreateNewPermissionGroup={() => {
            setCategory(PERMISSION_GROUP_CATEGORY.CLIENT);
            togglePermissionGroupsDrawer();
          }}
          permissionGroupOptionList={clientPermissionGroupOptionList}
          client={clientDetailsInfo.client}
          role={clientDetailsInfo.role}
          containerClass="assign-client-container-client-form"
          isCreateMode={true}
        />
        {!unsavedProjectList.length &&
          (!isAdminRoleSelected || showHCProjects) && (
            <Box className="project-selection-form">
              <ClientProjectSelection
                isAdmin={isAdminRoleSelected}
                clientProjects={clientProjects}
                clientHCProjects={clientHCProjects}
                showHCProjects={showHCProjects}
                setCurrentProject={(project: ProjectDetails) => {
                  setCreateProjectDetails(project);
                }}
                isCreateMode
                cancelButtonHidden={true}
                showPermissionGroupDropdown={showPermissionGroupDropdown}
                selectedClientId={selectedClientId}
                selectedClientBudgetEstimatedPermisionEnable={
                  clientBudgetConsumptionEnabled
                }
                isEditDisabled={
                  isControlDisabled ||
                  !selectedClientId ||
                  !selectedRoleId ||
                  (!isAdminRoleSelected && !selectedClientPermissionGroupId) ||
                  isClientExist()
                }
                buttonLabel={t('addEditTeamMember.form.buttonAssignProject')}
                permissionOptionList={projPermissionGroupOptionList}
                selectedPermissionGroup={selectedPermissionGroup}
                onCreateNewPermissionGroup={() => {
                  setCategory(PERMISSION_GROUP_CATEGORY.PROJECT);
                  setPermissionGroupOpen(true);
                }}
                onPermissionChange={(id) => {
                  onPermissionGroupChange(
                    id,
                    PERMISSION_GROUP_CATEGORY.PROJECT
                  );
                }}
                setIsWorkspace={setIsWorkspace}
              />
            </Box>
          )}
      </Box>
      <Box className="assign-clients-button">
        <AurButton
          classNames="typo-text buttonLarge_size submit-btn"
          type="exit"
          label={t('buttons.dontSaveUpdate')}
          onClick={handleCancelClick}
        />
        <AurButton
          classNames="typo-text buttonLarge_size submit-btn"
          onClick={() => {
            if (isAdminRoleSelected) {
              setShowClientAdminModal(true);
              return;
            }
            saveAndContinueHandler();
          }}
          type="continue"
          label={t('addEditTeamMember.form.addClient')}
          disabled={isControlDisabled || isClientAddDisable()}
          dataTestId="add-client"
        />
      </Box>
      <AurDialog
        dialogTitle={t('common.justToConfirm')}
        popupTitle={t('addEditTeamMember.promotePopup.title')}
        popupContent={t('addEditTeamMember.promotePopup.content')}
        labelActionOk={t('addEditTeamMember.promotePopup.buttonYes')}
        labelActionCancel={t('addEditTeamMember.promotePopup.buttonCancel')}
        onOk={handleClientAdminModalOkay}
        onCancel={handleClientAdminModalCancel}
        showDialog={showClientAdminModal}
        buttonStyle="sideBySide"
      />
    </Box>
  );

  // Accordion view
  const viewMode = (
    <Box
      className={` ${mode === 'edit' ? (isAdminRoleSelected ? 'padding-bottom' : 'edit-mode') : ''}`}
    >
      <ClientAccordionViewContainer
        mode={mode}
        clientInfoDetails={clientDetailsInfo}
        onEditButtonClicked={onEditButtonClicked}
        onDeleteButtonClicked={onDeleteButtonClicked}
        onProjectSavedOrCanceled={onProjectSavedOrCanceled}
        isControlDisabled={mode === 'edit' ? true : isControlDisabled}
        onEditProject={onEditProject}
        showHCProjects={showHCProjects}
        clientProjects={clientProjects}
        clientHCProjects={clientHCProjects}
        selectedPermissionGroup={selectedPermissionGroup}
        selectedClientId={selectedClientId}
        permissionFromDB={permissionFromDB}
        permissionGroupOptionList={clientPermissionGroupOptionList}
        projectPermissionGroupOptionList={projPermissionGroupOptionList}
        onPermissionGroupChange={(id) => {
          onPermissionGroupChange(id, PERMISSION_GROUP_CATEGORY.PROJECT);
        }}
        onPermissionGroupsUpdated={(savedPermissionGroup) => {
          if (savedPermissionGroup) {
            applySavedPermissionGroupToForm(
              savedPermissionGroup,
              PERMISSION_GROUP_CATEGORY.PROJECT
            );
          } else {
            void fetchPermissionGroups();
          }
        }}
        onAssignProjectClick={() => setSelectedPermissionGroup(null)}
      />
    </Box>
  );

  // client edit mode
  const editMode = (
    <>
      <AurAccordion
        fullWidth
        title={clientDetailsInfo.client}
        ref={accordionRef}
        alertWarningIcon={false}
        disabled={isControlDisabled}
        className="edit-client-details"
      >
        <Box className="labels-padding-left">
          <Grid container className="role-list-row">
            <Grid item xs={2}>
              <Typography className="typo-text subhead4_size" mt={3}>
                {t('addEditTeamMember.form.roleLabel')}
              </Typography>

              {!isAdminRoleSelected && (
                <Typography className="typo-text subhead4_size" mt={10}>
                  {t('addEditTeamMember.form.clientPermissionGroupLabel')}
                </Typography>
              )}
            </Grid>
            <Grid
              item
              xs={10}
              display={'flex'}
              justifyContent={'space-between'}
            >
              <ClientInfoForm
                formLabel=""
                selectedClientId={clientDetailsInfo.clientId}
                roleOptions={roleOptions}
                onRoleChange={onRoleChange}
                onPermissionGroupChange={(id) => {
                  onPermissionGroupChange(id, PERMISSION_GROUP_CATEGORY.CLIENT);
                }}
                onHCProjectChange={(id) => {
                  setSelectedHCProjectId(id);
                }}
                selectedRoleId={selectedRoleId}
                selectedHCProjectId={selectedHCProjectId}
                selectedPermissionGroupId={selectedClientPermissionGroupId}
                isEditDisabled={isControlDisabled}
                isValid={
                  (clientDetailsInfo.roleId !== selectedRoleId ||
                    (clientDetailsInfo.permissionGroupId || null) !==
                      (selectedClientPermissionGroupId || null) ||
                    (isAdminRoleSelected &&
                      clientDetailsInfo.roleId === selectedRoleId &&
                      !!selectedHCProjectId)) &&
                  (isAdminRoleSelected || !!selectedClientPermissionGroupId)
                }
                isAdminRoleSelected={isAdminRoleSelected}
                clientFieldHidden={true}
                buttonSectionHidden={false}
                hasHCProjects={clientHCProjects.length > 0}
                containerClass="edit-client-info-container"
                onSubmit={() => {
                  handleUserRoleChange();
                }}
                onCancel={() => {
                  handleCancelUserRole();
                }}
                onHCProjectCheckedChange={(checked: boolean) => {
                  setShowHCProjects && setShowHCProjects(checked);
                }}
                showHCProjects={showHCProjects}
                clientHCProjects={clientHCProjects}
                projectFieldHidden={clientDetailsInfo?.projectList?.some(
                  (p) => p.highlyConfidential
                )}
                onCreateNewPermissionGroup={() => {
                  setCategory(PERMISSION_GROUP_CATEGORY.CLIENT);
                  togglePermissionGroupsDrawer();
                }}
                permissionGroupOptionList={clientPermissionGroupOptionList}
                client={clientDetailsInfo.client}
                role={clientDetailsInfo.role}
              />
            </Grid>
          </Grid>

          {viewMode}
        </Box>
      </AurAccordion>
      <AurDialog
        popupTitle={t('addEditTeamMember.cancelEditPopup.title')}
        popupContent={t('addEditTeamMember.cancelEditPopup.content')}
        labelActionOk={t('addEditTeamMember.cancelEditPopup.buttonYes')}
        labelActionCancel={t('addEditTeamMember.cancelEditPopup.buttonCancel')}
        onOk={handleCancelModalOkay}
        onCancel={handleCancelModalCancel}
        showDialog={showCancelModal}
        buttonStyle="sideBySide"
      />
      <AurDialog
        dialogTitle={t('common.justToConfirm')}
        popupTitle={t('addEditTeamMember.promotePopup.title')}
        popupContent={t('addEditTeamMember.promotePopup.content')}
        labelActionOk={t('addEditTeamMember.promotePopup.buttonYes')}
        labelActionCancel={t('addEditTeamMember.promotePopup.buttonCancel')}
        onOk={handlePromoteModalOkay}
        onCancel={handlePromoteModalCancel}
        showDialog={showPromoteModal}
        buttonStyle="sideBySide"
      />
      <AurDialog
        popupTitle={t('addEditTeamMember.demotePopup.title')}
        popupContent={t('addEditTeamMember.demotePopup.content')}
        labelActionOk={t('addEditTeamMember.demotePopup.buttonYes')}
        labelActionCancel={t('addEditTeamMember.demotePopup.buttonCancel')}
        onOk={handleDemoteModalOkay}
        onCancel={handleDemoteModalCancel}
        showDialog={showDemoteModal}
        buttonStyle="sideBySide"
      />
    </>
  );

  let component = createMode;
  if (mode == 'edit') {
    component = editMode;
  } else if (mode == 'view') {
    component = viewMode;
  }

  return (
    <div data-testid="client-project-form-container">
      {component}
      {permissionGroupOpen && (
        <PermissionGroupDrawer
          shouldShow={permissionGroupOpen}
          onClose={togglePermissionGroupsDrawer}
          clientId={
            mode == 'edit' ? clientDetailsInfo.clientId : selectedClientId
          }
          clientName={''}
          budgetConsumptionEnabled={
            mode == 'edit'
              ? (clientDetailsInfo?.budgetConsumptionEnabled ?? false)
              : clientBudgetConsumptionEnabled
          }
          onUpdate={(savedPermissionGroup) => {
            if (savedPermissionGroup) {
              applySavedPermissionGroupToForm(savedPermissionGroup, category);
            } else {
              void fetchPermissionGroups();
            }
          }}
          showAddGrpOnOpen={true}
          category={category}
        />
      )}
      <AurLoadingIndicator overlay={true} isOpen={showLoader} />

      <AurDialog
        dialogTitle={t('dialog.areYouSure')}
        popupTitle={t('addEditTeamMember.domainValidationModal.title')}
        popupContent={t('addEditTeamMember.domainValidationModal.content')}
        labelActionOk={t('common.iAmSure')}
        labelActionCancel={t('common.cancel')}
        onOk={handleConfirmDelete}
        onCancel={handleCancelDelete}
        showDialog={openDeleteModal}
        buttonStyle="sideBySide"
        className="with-max-width"
      />
    </div>
  );
}

export default ClientAndProjectFormContainer;
